import { MatDateFormats } from '@angular/material/core';

/**
 * Converts an "HH:mm" (24h) time string into a Date on today's date, as required by
 * MatTimepicker (which works with the configured DateAdapter's date type, here `Date`).
 * Accepts an existing Date as-is. Returns null for empty/invalid input.
 */
export function hhmmToDate(value: string | Date | null | undefined): Date | null {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (!value) {
    return null;
  }
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const hours = +match[1];
  const minutes = +match[2];
  if (hours > 23 || minutes > 59) {
    return null;
  }
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/**
 * Converts a MatTimepicker value (a Date, or an untouched "HH:mm" string) back into an
 * "HH:mm" (24h) string for the API. Returns "" for empty/invalid input.
 */
export function dateToHhmm(value: Date | string | null | undefined): string {
  if (value == null || value === '') {
    return '';
  }
  const date = value instanceof Date ? value : hhmmToDate(value);
  if (!date || isNaN(date.getTime())) {
    return '';
  }
  const hours = ('0' + date.getHours()).slice(-2);
  const minutes = ('0' + date.getMinutes()).slice(-2);
  return `${hours}:${minutes}`;
}

/**
 * 24-hour MAT_DATE_FORMATS for MatTimepicker. Provided at component level so the timepicker
 * always renders and parses 24h HH:mm regardless of locale (the previous
 * ngx-material-timepicker used [format]="24"). The date formats are kept for completeness
 * (MatTimepicker validates that a full MatDateFormats is present) but are unused here.
 */
export const TIMEPICKER_24H_FORMATS: MatDateFormats = {
  parse: {
    dateInput: null,
    timeInput: null,
  },
  display: {
    dateInput: { year: 'numeric', month: 'numeric', day: 'numeric' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
    timeInput: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
    timeOptionLabel: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  },
};
